import { Dashboard } from '@aidkitorg/aidkit/lib/admin_panel/dashboards';
import { Action, Button, Card, CardBody, DataTable, Icon, Modal, Paragraph } from '@aidkitorg/component-library';
import { ColumnDef } from '@tanstack/react-table';
import React, { useState } from 'react';
import { useDashboards } from '../../../Contexts/DashboardsContext';
import { useLocalizedStrings } from '../../../Localization';
import { useHistory } from 'react-router-dom';
import { useAsyncEffect } from '../../../Hooks/AsyncEffect';
import { useCopyToClipboard } from '../../../Hooks/CopyToClipboard';

export const DashboardsDataTable = () => {
  const L = useLocalizedStrings()
  const history = useHistory()
    
  const { dashboards, isDashboardsLoading, getDashboards, deleteDashboardById } = useDashboards()

  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [dashboardToDelete, setDashboardToDelete] = useState<Dashboard | null>()
  const [isDeleting, setIsDeleting] = useState(false)

  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [dashboardToShare, setDashboardToShare] = useState<Dashboard | null>()
  const { copied, copyToClipboard } = useCopyToClipboard();
  
  useAsyncEffect(async () => {
    getDashboards()
  }, []);

  const DASHBOARD_COLUMNS: ColumnDef<Dashboard>[] = [
    {
      accessorKey: 'title',
      header: L.admin_panel.dashboards.dashboard_name,
      cell: ({ row }) => {
        let dashboardId = row.original.id
        if (row.original.href) {
          const match = row.original.href.match(/\/d\/(.+)$/);
          dashboardId =  match ? match[1] : row.original.id;
        }
        return (
          <Button
            className="font-medium text-left"
            variant="link"
            onClick={(e) => {
              // URL encode the title to preserve original capitalization and special characters
              const encodedTitle = encodeURIComponent(row.original.title);
              const url = `/admin-panel/dashboards/${dashboardId}/${encodedTitle}`
              // Add type=survey query param for survey dashboards
              if (row.original.source === 'survey') {
                history.push(url + "?type=survey")
              } else {
                history.push(url)
              }
            }}
          >
            {row.original.title}
          </Button>
        )
      }
    },
    {
      accessorKey: 'description',
      header: L.admin_panel.dashboards.description,
      cell: ({ row }) => (
        <span className="text-gray-700">{row.original.description}</span>
      )
    }, 
    {
      accessorKey: 'updated_at',
      header: L.admin_panel.dashboards.updated_on,
      cell: ({ row }) => {
        if (!row.original.updated_at) return "--"
        const date = new Date(row.original.updated_at)
        const formatted = new Intl.DateTimeFormat("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }).format(date)
  
        return <span>{formatted}</span>
      },
    },
    {
      accessorKey: 'created_by',
      header: L.admin_panel.dashboards.created_by,
      cell: ({ row }) => {
        if (row.original.created_by === 'System') {
          return L.admin_panel.dashboards.system
        } else {
          return row.original.created_by
        }
      },
    }
  ]

  const handleShareClick = (dashboard: Dashboard) => {
    setDashboardToShare(dashboard)
    setShareModalOpen(true)
  }

  const handleDeleteClick = (dashboard: Dashboard) => {
    setDashboardToDelete(dashboard)
    setDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!dashboardToDelete) return

    setIsDeleting(true)
    try {
      await deleteDashboardById(dashboardToDelete.id)
      setDeleteModalOpen(false)
      setDashboardToDelete(null)
    } catch (error) {
      // Error handling is done in the context
    } finally {
      setIsDeleting(false)
    }
  }

  const handleCancelDelete = () => {
    setDeleteModalOpen(false)
    setDashboardToDelete(null)
  }

  const dashboardActions: Array<Action<Dashboard>> = [
    {
      label: L.admin_panel.dashboards.view,
      onClick: (row: Dashboard) => {
        let dashboardId = row.id
        if (row.href) {
          const match = row.href.match(/\/d\/(.+)$/);
          dashboardId = match ? match[1] : row.id;
        }
        // URL encode the title to preserve original capitalization and special characters
        const encodedTitle = encodeURIComponent(row.title);
        const url = `/admin-panel/dashboards/${dashboardId}/${encodedTitle}`
        // Add type=survey query param for survey dashboards
        if (row.source === 'survey') {
          history.push(url + "?type=survey")
        } else {
          history.push(url)
        }
      },
      icon: 'EyeIcon'
    },
    {
      label: L.admin_panel.dashboards.edit,
      onClick: (row: Dashboard) => {
        history.push(`/admin-panel/dashboards/${row.id}/edit`)
      },
      icon: 'PencilIcon',
      hideWhen: (row: Dashboard) => {
        return row.view_only === true
      }
    },
    {
      label: L.admin_panel.dashboards.share,
      onClick: (row: Dashboard) => {
        handleShareClick(row)
      },
      icon: 'LinkIcon'
    },
    {
      label: L.admin_panel.dashboards.delete,
      onClick: (row: Dashboard) => {
        handleDeleteClick(row)
      },
      icon: 'TrashIcon',
      variant: 'warn',
      hideWhen: (row: Dashboard) => {
        return row.view_only === true
      },
    }
  ]

  const dashboardsEmptyState = (
    <div className="py-2 text-center text-gray-600 flex flex-col gap-y-4">
      <div>
        {L.admin_panel.dashboards.dashboards_empty_msg}
      </div>
      <div>
        <Button 
          onClick={(e) => {
            history.push('/admin-panel/dashboards/new')
          }}
        >
          {L.admin_panel.dashboards.create_new_dashboard}
        </Button>
      </div>
    </div>
  )

  const deleteConfirmation = (
    <Modal 
      title={L.admin_panel.dashboards.delete_dashboard}
      open={deleteModalOpen} 
      onClose={() => setDeleteModalOpen(false)}
    >
      <div className="mb-4">
        {L.admin_panel.dashboards.are_you_sure_delete.replace('{dashboardName}', dashboardToDelete?.title ?? '')}
      </div>
      <div className="flex justify-end gap-2">
        <Button
          variant="secondary"
          onClick={handleCancelDelete}
          disabled={isDeleting}
        >
          {L.admin_panel.dashboards.cancel}
        </Button>
        <Button
          variant="danger"
          onClick={handleConfirmDelete}
          disabled={isDeleting}
        >
          {L.admin_panel.dashboards.delete}
        </Button>
      </div>
    </Modal>
  )

  const handleCopy = async () => {
    let shareLink = `${window.location.origin}/admin-panel/dashboards/${dashboardToShare?.id}`

    if (dashboardToShare?.source === 'survey') {
      const encodedTitle = encodeURIComponent(dashboardToShare?.title || "");
      let surveyDashboardId = ''
      if (dashboardToShare?.href) {
        const match = dashboardToShare.href.match(/\/d\/(.+)$/);
        surveyDashboardId =  match ? match[1] : dashboardToShare?.id;
      }
      const surveyUrl = `/admin-panel/dashboards/${surveyDashboardId}/${encodedTitle}?type=survey`

      shareLink = `${window.location.origin}${surveyUrl}`
    }

    await copyToClipboard(shareLink);
  };

  const shareModal = (
    <Modal 
      size="sm"
      title={L.admin_panel.dashboards.share_dashboard}
      open={shareModalOpen}
      onClose={() => setShareModalOpen(false)}
    >
      <div>
        <Paragraph>
          {L.admin_panel.dashboards.share_dashboard_msg}
        </Paragraph>

        <div className="flex justify-center">
          <Button 
            icon={
              <Icon
                name={copied ? "CheckIcon" : "LinkIcon"}
                role="decorative"
              />}
            variant="secondary"
            onClick={handleCopy}
          >
            {copied ? L.admin_panel.dashboards.link_copied : L.admin_panel.dashboards.copy_link}
          </Button>
        </div>
      </div>
    </Modal>
  )

  const dashboardsEmpty = !isDashboardsLoading && dashboards.length === 0

  return (
    <Card className="rounded-md w-full h-full">
      <CardBody>
        {dashboardsEmpty ? dashboardsEmptyState : 
          <DataTable
            columns={DASHBOARD_COLUMNS}
            actions={dashboardActions}
            data={dashboards}
            enableSearch
            searchLabel={L.admin_panel.dashboards.search_dashboards}
            dataFilteredOutMessage={L.admin_panel.dashboards.no_dashboards_match}
            loading={isDashboardsLoading}
            showPagination
          />
        }
        {deleteConfirmation}
        {shareModal}
      </CardBody>
    </Card>
  )
}